import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";
import { adminUploadProof, adminSupplyStatus, adminSetTracking, adminFetchBlob, extractApiError } from "@/lib/api";
import { SUPPLY_LABELS, proofStatusText } from "@/components/booking/SupplyPortalPanel";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CUSTOM_LABELS = { name: "Name", age: "Age", school: "School", grad_year: "Grad year", colors: "Colors", theme: "Theme", wording: "Wording", instructions: "Instructions", event_name: "Feature" };

export const SupplyQuoteFields = ({ v, setV, lead }) => {
  const items = v.supply_items;
  const upd = (i, k, val) => setV({ ...v, supply_items: items.map((it, j) => (j === i ? { ...it, [k]: val } : it)) });
  const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const total = subtotal + (v.fulfillment === "delivery" ? Number(v.delivery_charge) || 0 : 0) + (v.fulfillment === "shipping" ? Number(v.shipping_charge) || 0 : 0);
  return (
    <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--ss-border)" }} data-testid="approve-supply">
      <div className="font-label text-[0.6rem] mb-3" style={{ color: "var(--ss-gold)" }}>Social Supply custom items · 100% due at purchase</div>
      {lead && <div className="text-xs mb-3" data-testid="supply-admin-lead">Minimum lead time: <strong>{lead.days} calendar days</strong> ({lead.reason})</div>}
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 mb-2">
          <input className="editorial-input text-sm col-span-12 sm:col-span-4" placeholder="Product" value={it.product} onChange={(e) => upd(i, "product", e.target.value)} data-testid={`supply-item-product-${i}`} />
          <input className="editorial-input text-sm col-span-3 sm:col-span-2" type="number" min="1" placeholder="Qty" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} data-testid={`supply-item-qty-${i}`} />
          <input className="editorial-input text-sm col-span-5 sm:col-span-3" placeholder="Customization / add-ons" value={it.customization} onChange={(e) => upd(i, "customization", e.target.value)} data-testid={`supply-item-custom-${i}`} />
          <input className="editorial-input text-sm col-span-3 sm:col-span-2" type="number" step="0.01" placeholder="Unit $" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} data-testid={`supply-item-price-${i}`} />
          <button type="button" className="col-span-1" onClick={() => setV({ ...v, supply_items: items.filter((_, j) => j !== i) })} aria-label="Remove item"><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" className="font-label text-[0.6rem] inline-flex items-center gap-1 mb-4" data-testid="supply-add-item"
              onClick={() => setV({ ...v, supply_items: [...items, { product: "", quantity: 1, customization: "", unit_price: "" }] })}>
        <Plus size={12} /> Add item
      </button>
      <div className="grid md:grid-cols-3 gap-3 text-sm">
        <label>Needed-by<input type="date" className="editorial-input" value={v.needed_by} onChange={(e) => setV({ ...v, needed_by: e.target.value })} data-testid="supply-needed-by" /></label>
        <label>Fulfillment
          <select className="editorial-input" value={v.fulfillment} onChange={(e) => setV({ ...v, fulfillment: e.target.value })} data-testid="supply-fulfillment">
            <option value="">Choose…</option><option value="pickup">Pickup (free)</option><option value="delivery">Local delivery</option><option value="shipping">Shipping</option><option value="event">Executive — at event (with nightclub)</option>
          </select>
        </label>
        {v.fulfillment === "pickup" && (
          <label>Pickup location
            <select className="editorial-input" value={v.pickup_location} onChange={(e) => setV({ ...v, pickup_location: e.target.value })} data-testid="supply-pickup-location">
              <option value="">Choose…</option><option value="chicago">Chicago</option><option value="stl">St. Louis</option>
            </select>
          </label>
        )}
        {v.fulfillment === "delivery" && (
          <label>Delivery charge ($)<input type="number" step="0.01" className="editorial-input" value={v.delivery_charge} onChange={(e) => setV({ ...v, delivery_charge: e.target.value })} data-testid="supply-delivery-charge" /></label>
        )}
        {v.fulfillment === "shipping" && (
          <label>Shipping charge ($, required)<input type="number" step="0.01" min="0" className="editorial-input" value={v.shipping_charge} onChange={(e) => setV({ ...v, shipping_charge: e.target.value })} data-testid="supply-shipping-charge" /></label>
        )}
      </div>
      {["delivery", "shipping", "event"].includes(v.fulfillment) && (
        <div className="grid md:grid-cols-4 gap-3 text-sm mt-3">
          <input className="editorial-input md:col-span-3" placeholder={{ shipping: "Shipping address", event: "Event address" }[v.fulfillment] || "Delivery address"} value={v.delivery_address} onChange={(e) => setV({ ...v, delivery_address: e.target.value })} data-testid="supply-delivery-address" />
          {v.fulfillment !== "event" && (
            <select className="editorial-input" value={v.delivery_state} onChange={(e) => setV({ ...v, delivery_state: e.target.value })} data-testid="supply-delivery-state">
              <option value="">State…</option><option value="IL">Illinois</option><option value="MO">Missouri</option><option value="OTHER">Other</option>
            </select>
          )}
        </div>
      )}
      <div className="mt-3 text-sm">Social Supply total <strong data-testid="supply-total">{money(total)}</strong></div>
    </div>
  );
};

const RequestInfo = ({ d, token }) => {
  const sr = d.booking.supply_request;
  const open = async (f) => {
    const blob = await adminFetchBlob(token, `/admin/bookings/${d.booking.id}/photos/${f.id}`);
    window.open(URL.createObjectURL(blob), "_blank");
  };
  return (
    <div className="text-xs mb-3 space-y-1" style={{ color: "var(--ss-cream-dim)" }} data-testid="supply-request-info">
      {sr.package && <div>Package: <strong>{sr.package.replace(/_/g, " ")}</strong>{sr.package_addons?.length ? ` + ${sr.package_addons.join(", ")}` : ""}</div>}
      {(sr.estimate?.lines || []).length > 0 && <div>Requested: {sr.estimate.lines.map((l) => `${l.label}${l.quantity > 1 && !l.key.includes(":") ? ` × ${l.quantity}` : ""}`).join("; ")}</div>}
      {sr.estimate && <div>Customer estimate: {money(sr.estimate.estimated_total)}{sr.estimate.needs_review?.length ? ` (review: ${sr.estimate.needs_review.join(", ")})` : ""}</div>}
      {sr.details && <div>Client notes: {sr.details}</div>}
      {Object.entries(sr.customization || {}).map(([k, val]) => <div key={k}>{CUSTOM_LABELS[k] || k}: {val}</div>)}
      <div>Needed by: {sr.needed_by || "—"} · {sr.fulfillment || "—"} {{ chicago: "Chicago", stl: "St. Louis" }[sr.pickup_location] || sr.delivery_address || sr.shipping_address || sr.event_address}{sr.package_2xl ? ` · 2XL+ shirts: ${sr.package_2xl}` : ""}</div>
      {(d.design_files || []).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="supply-design-files">Files: {d.design_files.map((f) => (
          <button key={f.id} className="underline" onClick={() => open(f)} data-testid={`supply-design-file-${f.id}`}>{f.original_filename}</button>
        ))}</div>
      )}
    </div>
  );
};

const NEXT = { proof_approved: ["in_production"], in_production: { pickup: ["ready"], delivery: ["out_for_delivery", "ready"], shipping: ["shipped"], event: ["ready"] }, ready: ["delivered"], out_for_delivery: ["delivered"], shipped: ["delivered"] };

const Tracking = ({ b, token, onDone }) => {
  const [t, setT] = useState({ carrier: b.tracking?.carrier || "", tracking_number: b.tracking?.tracking_number || "", tracking_url: b.tracking?.tracking_url || "" });
  const save = async () => {
    try { await adminSetTracking(token, b.id, t); toast.success("Tracking saved — client emailed"); onDone(); } catch (e) { toast.error(extractApiError(e)); }
  };
  return (
    <div className="mt-3 grid md:grid-cols-4 gap-2 text-sm" data-testid="supply-tracking-form">
      <input className="editorial-input" placeholder="Carrier" value={t.carrier} onChange={(e) => setT({ ...t, carrier: e.target.value })} data-testid="tracking-carrier" />
      <input className="editorial-input" placeholder="Tracking number" value={t.tracking_number} onChange={(e) => setT({ ...t, tracking_number: e.target.value })} data-testid="tracking-number" />
      <input className="editorial-input" placeholder="Tracking link (optional)" value={t.tracking_url} onChange={(e) => setT({ ...t, tracking_url: e.target.value })} data-testid="tracking-url" />
      <button className="btn-outline" disabled={t.tracking_number.trim().length < 3} onClick={save} data-testid="tracking-save">Save tracking</button>
    </div>
  );
};

export const SupplyAdminPanel = ({ d, token, onDone }) => {
  const b = d.booking;
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmMove, setConfirmMove] = useState("");
  const status = b.supply_status;
  const fulfil = b.quote?.fulfillment;
  const upload = async () => {
    setBusy(true);
    try { await adminUploadProof(token, b.id, file, note); toast.success("Proof sent to client"); setFile(null); setNote(""); onDone(); }
    catch (e) { toast.error(extractApiError(e, "Upload failed")); }
    setBusy(false);
  };
  const move = async (s) => {
    if (confirmMove !== s) { setConfirmMove(s); return; }
    setConfirmMove("");
    try { await adminSupplyStatus(token, b.id, s); toast.success(SUPPLY_LABELS[s]); onDone(); } catch (e) { toast.error(extractApiError(e)); }
  };
  const open = async (p) => {
    const blob = await adminFetchBlob(token, `/admin/bookings/${b.id}/proofs/${p.id}/file`);
    window.open(URL.createObjectURL(blob), "_blank");
  };
  const n = NEXT[status];
  const nexts = Array.isArray(n) ? n : (n && n[fulfil]) || (n ? ["ready"] : []);
  return (
    <div className="p-5 border" style={{ borderColor: "var(--ss-gold)" }} data-testid="supply-admin-panel">
      <div className="font-label text-[0.6rem] mb-3" style={{ color: "var(--ss-gold)" }}>Social Supply production</div>
      <div className="text-sm mb-3">Status: <strong data-testid="supply-status">{status ? SUPPLY_LABELS[status] : "Awaiting signature + full payment"}</strong></div>
      {b.supply_request && <RequestInfo d={d} token={token} />}
      <div className="text-xs mb-2" data-testid="supply-revision-rounds">Revision rounds used: {d.revision_rounds_used} of {d.revisions_included} included{b.revision_fee_due && <strong style={{ color: "var(--ss-rust)" }}> · Round {b.revision_fee_due.round} fee {money(b.revision_fee_due.amount)} awaiting client payment</strong>}</div>
      {(b.revision_fees_paid || []).map((r, i) => <div key={i} className="text-xs">Revision round {r.round} fee paid {money(r.amount)} · {new Date(r.paid_at).toLocaleString()}</div>)}
      {(d.proofs || []).map((p) => (
        <div key={p.id} className="text-sm flex flex-wrap gap-2 items-center py-1" data-testid={`admin-proof-v${p.version}`}>
          <button className="underline" onClick={() => open(p)}>Proof v{p.version} (original)</button>
          <span style={{ color: "var(--ss-muted)" }} data-testid={`admin-proof-status-v${p.version}`}>{proofStatusText(p)}{(p.approved_ip || p.responded_ip) && ` · IP ${p.approved_ip || p.responded_ip}`}</span>
          {p.client_comment && <span className="text-xs">“{p.client_comment}”</span>}
        </div>
      ))}
      {["paid_confirmed", "proof_sent", "changes_requested"].includes(status) && !b.revision_fee_due && (
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <label className="btn-outline inline-flex items-center gap-2 cursor-pointer">
            <Upload size={14} /> {file ? file.name : "Choose proof (image or PDF)"}
            <input type="file" accept="image/*,application/pdf" className="sr-only" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="proof-file-input" />
          </label>
          <input className="editorial-input flex-1 text-sm" placeholder="Note to client (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button className="btn-primary" disabled={!file || busy} onClick={upload} data-testid="proof-upload-btn">{busy ? "Sending\u2026" : `Send proof v${(d.proofs || []).length + 1}`}</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {nexts.map((s) => (
          <button key={s} className="btn-primary mt-3" onClick={() => move(s)} data-testid={`supply-move-${s}`}>
            {confirmMove === s ? `Confirm: move to ${SUPPLY_LABELS[s]} (client is emailed)` : `Move to ${SUPPLY_LABELS[s]}`}
          </button>
        ))}
      </div>
      {fulfil === "shipping" && ["shipped", "delivered"].includes(status) && <Tracking b={b} token={token} onDone={onDone} />}
    </div>
  );
};
